import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GameState, StarSystem, Fleet, FleetId, SystemType } from './types';
import { TRAVEL_DISTANCE_PER_TICK, MAP_SCALE } from './shared/constants';
import { getPlanetTypeDefaults, getPlanetOverrideBySystem } from './shared/planetConfig';
import Tooltip from './ui/Tooltip';
import type { BorderOptions, Vec2, BorderPolygon } from './borders/types';

interface MapProps {
  gameState: GameState;
  onSystemClick: (system: StarSystem) => void;
  onFleetClick: (fleet: Fleet) => void;
  myPlayerId?: string | null;
  selectedFleets?: FleetId[];
  selectedSystemId?: number | null;
  ping?: { x: number; y: number } | null;
  onFleetContextMenu?: (fleet: Fleet, clientX: number, clientY: number) => void;
  onSystemContextMenu?: (system: StarSystem, clientX: number, clientY: number) => void;
  onEmptyClick?: () => void;
  showRegionLabels?: boolean;
  showEmpireBorders?: boolean;
  // Fraction of current server tick [0,1] for sub-tick interpolation
  tickFraction?: number;
}

const MAP_WIDTH = 800;
const MAP_HEIGHT = 600;

const StarSystemComponent: React.FC<{ system: StarSystem; myPlayerId?: string | null; isSelected?: boolean; tick?: number; getOwnerColor: (owner: string) => string; getOwnerName: (owner: string) => string }> = ({ system, myPlayerId, isSelected = false, tick = 0, getOwnerColor, getOwnerName }) => {
  // Inner core color for the star marker
  let coreColor = '#e6edf3';
  if (system.owner === 'NPC') {
    coreColor = '#9fb1c5';
  } else if (system.owner === 'Unclaimed') {
    coreColor = '#e6edf3';
  } else if (system.owner === myPlayerId) {
    coreColor = '#00ff00';
  } else {
    coreColor = '#66bbff';
  }

  // Ownership halo and label tint by allegiance
  const isMine = system.owner === myPlayerId;
  const isNPC = system.owner === 'NPC';
  const isUnclaimed = !system.owner || system.owner === 'Unclaimed';
  const haloFill = isMine
    ? 'url(#haloAlly)'
    : (isNPC || isUnclaimed ? 'url(#haloNeutral)' : 'url(#haloEnemy)');

  const labelFill = isNPC || isUnclaimed ? 'var(--text)' : getOwnerColor(String(system.owner));
  const ownerName = (!isNPC && !isUnclaimed) ? getOwnerName(String(system.owner)) : null;
  
  const typeFill = (() => {
    switch (system.planetType) {
      case 'terran': return '#3b82f6';
      case 'desert': return '#f59e0b';
      case 'ice': return '#60a5fa';
      case 'volcanic': return '#ef4444';
      case 'gas_giant': return '#a78bfa';
      default: return '#e6edf3';
    }
  })();

  return (
    <g transform={`translate(${system.position.x}, ${system.position.y})`}>
      {/* Ownership halo */}
      <circle cx="0" cy="0" r="18" fill={haloFill} />
      {/* Planet core (simple colored circle by type) */}
      <circle cx="0" cy="0" r="9" fill={typeFill} stroke="#e5e7eb" />
      {isSelected && (
        <circle cx="0" cy="0" r="16" fill="none" stroke="#ffd700" strokeWidth={2.5} />
      )}
      <text
        x="17"
        y="5"
        fill={labelFill}
        fontSize="12"
        fontWeight="bold"
        stroke="#000"
        strokeWidth={3}
        style={{ paintOrder: 'stroke' as const }}
      >
        {system.name}
      </text>
      {ownerName && (
        <text
          x="17"
          y="17"
          fill={labelFill}
          opacity={0.9}
          fontSize="10"
          stroke="#000"
          strokeWidth={3}
          style={{ paintOrder: 'stroke' as const }}
        >
          {ownerName}
        </text>
      )}
      
      {/* Shipyard icon - revert to simple wrench/gear badge */}
      {system.hasShipyard && (
        <g transform="translate(-5, -20)">
          <rect x="0" y="0" width="10" height="10" fill="#444" stroke="#888" strokeWidth="0.5" rx="1" />
          <text x="5" y="8" fill="#ffd700" fontSize="8" textAnchor="middle" fontWeight="bold">⚙</text>
        </g>
      )}
      
      {/* Construction indicator: animated ring + badge with ETA */}
      {system.constructionQueue && system.constructionQueue.length > 0 && (
        <>
          {(() => {
            const job = system.constructionQueue![0];
            const eta = Math.max(0, (job.completionTick || tick) - tick);
            const jobIcon = job.type === 'ship' ? '🚀' : job.type === 'shipyard' ? '⚙️' : job.type === 'repair' ? '🔧' : '🏗️';
            const jobColor = job.type === 'ship' ? '#00e676' : job.type === 'shipyard' ? '#ffd93d' : job.type === 'repair' ? '#ffb74d' : '#61dafb';
            const label = `${jobIcon} t${eta}`;
            const w = Math.max(32, label.length * 6 + 10);
            return (
              <g>
                <circle cx="0" cy="0" r="18" fill="none" stroke={jobColor} strokeWidth="2" strokeDasharray="6 4" opacity="0.9">
                  <animate attributeName="stroke-dashoffset" from="0" to="-80" dur="2s" repeatCount="indefinite" />
                </circle>
                <g transform={`translate(${-26}, ${-26})`}>
                  <rect x={-w/2} y={-10} width={w} height={16} rx={8} ry={8} fill="#0a0a0a" fillOpacity="0.75" stroke={jobColor} />
                  <text x={0} y={2} textAnchor="middle" fontSize="10" fill={jobColor} fontWeight="bold">{label}</text>
                </g>
              </g>
            );
          })()}
        </>
      )}
    </g>
  );
};

const FleetComponent: React.FC<{ 
  fleet: Fleet; 
  x: number; 
  y: number; 
  myPlayerId?: string | null;
  isSelected: boolean;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  color: string;
  icon?: string;
  ownerName?: string;
}> = ({ fleet, x, y, myPlayerId, isSelected, onClick, onContextMenu, color, icon = 'chevron', ownerName }) => {
  const fleetColor = color;
  
  const healthPercentage = fleet.maxPower ? Math.max(0, Math.min(100, (fleet.power / fleet.maxPower) * 100)) : 100;
  const healthBarColor = healthPercentage > 60 ? '#00ff00' : 
                         healthPercentage > 30 ? '#ffaa00' : '#ff3333';
  
  const iconPath = (() => {
    switch (icon) {
      case 'triangle':
        return 'M -12 8 L 12 8 L 0 -10 Z';
      case 'diamond':
        return 'M 0 -12 L 12 0 L 0 12 L -12 0 Z';
      case 'wing':
        return 'M -16 4 L -4 -8 L 12 -2 L -2 10 Z';
      case 'chevron':
      default:
        return 'M -14 6 L 0 -8 L 14 6 Z';
    }
  })();

  return (
    <g
      transform={`translate(${x}, ${y})`}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu && onContextMenu(e); }}
      style={{ cursor: 'pointer' }}
    >
      {/* Fleet chevron with glow */}
      <g filter="url(#glow)">
        <path d={iconPath} fill={fleetColor} stroke={isSelected ? '#ffd700' : (fleet.inCombat ? '#ff4d4d' : '#e2e8f0')} strokeWidth={isSelected ? 2.5 : 1.2} />
        <path d="M -6 6 L 0 0 L 6 6 Z" fill={healthBarColor} opacity={0.85} />
      </g>
      
      {/* Compact inline health bar */}
      <rect x="-12" y="8.5" width="24" height="2.5" fill="#1f2937" rx="1" />
      <rect x="-12" y="8.5" width={24 * (healthPercentage / 100)} height="2.5" fill={healthBarColor} rx="1" />
      
      {/* Fleet power text */}
      <text x="0" y="2" textAnchor="middle" fontSize="9" fill="#ffffff" fontWeight="bold">{Math.round(fleet.power)}</text>

      {/* Owner name above icon (compact) */}
      {ownerName && (
        <text
          x={0}
          y={-14}
          textAnchor="middle"
          fontSize="9"
          fill="#e5e7eb"
          stroke="#000"
          strokeWidth={3}
          style={{ paintOrder: 'stroke' as const }}
        >
          {ownerName.length > 12 ? ownerName.slice(0, 11) + '…' : ownerName}
        </text>
      )}
      
      {/* Combat indicator */}
      {fleet.inCombat && (
        <circle cx="14" cy="-4" r="4" fill="red" stroke="white" strokeWidth="1">
          <animate attributeName="opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
};

const MapComponent: React.FC<MapProps> = ({ gameState, onSystemClick, onFleetClick, myPlayerId, selectedFleets = [], selectedSystemId = null, ping = null, onFleetContextMenu, onSystemContextMenu, onEmptyClick, showRegionLabels = true, showEmpireBorders = true, tickFraction = 0 }) => {
  if (!gameState) return null;

  const systemMap = new Map(gameState.systems.map(s => [s.id, s]));
  const playersById = useMemo(() => new Map(gameState.players.map(p => [p.id, p])), [gameState.players]);
  const getOwnerColor = (owner: string): string => {
    if (!owner) return '#9fb1c5';
    if (owner === 'NPC') return '#ff6666';
    if (owner === 'Unclaimed') return '#9fb1c5';
    const p = playersById.get(owner);
    if (p && p.color && typeof p.color === 'string' && p.color.trim()) return p.color.trim();
    // Fallbacks
    if (owner === myPlayerId) return '#4dd0e1';
    return '#66bbff';
  };
  const getOwnerIcon = (owner: string): string => {
    const p = playersById.get(owner);
    return (p?.fleetIcon as string) || 'chevron';
  };
  const getOwnerName = (owner: string): string => {
    if (!owner) return '—';
    if (owner === 'NPC' || owner === 'Unclaimed') return owner;
    const p = playersById.get(owner);
    return p?.name || owner;
  };
  const getFactionNameForSystem = (system: StarSystem | undefined | null): string | null => {
    if (!system) return null;
    const fid = (system as any).npcFactionId as string | undefined;
    if (!fid) return null;
    const fac = (gameState as any)?.mapMeta?.npcFactions?.find((f: any) => f && f.id === fid);
    return fac?.name || null;
  };
  // Pan & Zoom state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panningRef = useRef(false);
  const didPanRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [viewport, setViewport] = useState<{ width: number; height: number }>({ width: MAP_WIDTH, height: MAP_HEIGHT });
  const roRef = useRef<ResizeObserver | null>(null);
  const parentElRef = useRef<Element | null>(null);
  const [hoveredSystemId, setHoveredSystemId] = useState<number | null>(null);
  const [hoveredEdgeKey, setHoveredEdgeKey] = useState<string | null>(null);

  // Auto-fit galaxy to viewport after layout/viewport changes
  useEffect(() => {
    if (!gameState?.systems?.length || !viewport.width || !viewport.height) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of gameState.systems) {
      if (!s?.position) continue;
      minX = Math.min(minX, s.position.x);
      maxX = Math.max(maxX, s.position.x);
      minY = Math.min(minY, s.position.y);
      maxY = Math.max(maxY, s.position.y);
    }
    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) return;
    const worldW = Math.max(1, maxX - minX);
    const worldH = Math.max(1, maxY - minY);
    const pad = 100; // screen padding
    const sx = (viewport.width - pad * 2) / worldW;
    const sy = (viewport.height - pad * 2) / worldH;
    const newScale = Math.max(0.2, Math.min(2.5, Math.min(sx, sy)));
    setScale(newScale);
    setOffset({ x: 0, y: 0 });
  }, [gameState.systems.length, viewport.width, viewport.height]);

  // Geometry helpers for territory polygons
  type Point = { x: number; y: number };
  const computeConvexHull = (points: Point[]): Point[] => {
    if (points.length <= 1) return points.slice();
    const pts = points
      .map(p => ({ x: p.x, y: p.y }))
      .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
    const cross = (o: Point, a: Point, b: Point) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower: Point[] = [];
    for (const p of pts) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper: Point[] = [];
    for (let i = pts.length - 1; i >= 0; i--) {
      const p = pts[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper);
  };

  const expandPolygon = (poly: Point[], padding: number): Point[] => {
    if (poly.length === 0) return poly;
    const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
    const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
    return poly.map(p => {
      const dx = p.x - cx;
      const dy = p.y - cy;
      const len = Math.hypot(dx, dy) || 1;
      const k = (len + padding) / len;
      return { x: cx + dx * k, y: cy + dy * k };
    });
  };

  const smoothPolygon = (poly: Point[], iterations: number = 2): Point[] => {
    if (poly.length < 3) return poly;
    let pts = poly.slice();
    const chaikin = (input: Point[]): Point[] => {
      const out: Point[] = [];
      for (let i = 0; i < input.length; i++) {
        const a = input[i];
        const b = input[(i + 1) % input.length];
        const q = { x: 0.75 * a.x + 0.25 * b.x, y: 0.75 * a.y + 0.25 * b.y };
        const r = { x: 0.25 * a.x + 0.75 * b.x, y: 0.25 * a.y + 0.75 * b.y };
        out.push(q, r);
      }
      return out;
    };
    for (let k = 0; k < iterations; k++) pts = chaikin(pts);
    return pts;
  };

  const polygonPath = (poly: Point[]): string => {
    if (!poly.length) return '';
    return 'M ' + poly.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z';
  };

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  const adjustColor = (hex: string, factor: number): string => {
    if (!hex || typeof hex !== 'string') return hex;
    let value = hex.replace('#', '');
    if (value.length === 3) value = value.split('').map(c => c + c).join('');
    if (value.length !== 6) return hex;
    const r = clamp(Math.round(parseInt(value.slice(0, 2), 16) * factor), 0, 255);
    const g = clamp(Math.round(parseInt(value.slice(2, 4), 16) * factor), 0, 255);
    const b = clamp(Math.round(parseInt(value.slice(4, 6), 16) * factor), 0, 255);
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  // Build a proper concave hull that follows point distribution
  // Uses an alpha-shape-like approach with edge length filtering
  const computeConcaveHull = (points: Point[], maxEdgeLength: number): Point[] => {
    if (points.length === 0) return points;
    if (points.length < 3) return points;
    
    // Start with convex hull
    const hull = computeConvexHull(points);
    if (hull.length < 3) return hull;
    
    // Build a set of points not yet in hull
    const hullSet = new Set(hull.map(p => `${p.x},${p.y}`));
    const interior = points.filter(p => !hullSet.has(`${p.x},${p.y}`));
    
    // Iteratively add points that create edges within maxEdgeLength
    let improved = true;
    let currentHull = [...hull];
    let iterations = 0;
    const maxIterations = Math.min(50, points.length * 2);
    
    while (improved && interior.length > 0 && iterations < maxIterations) {
      improved = false;
      iterations++;
      
      // Try to insert each interior point
      for (let pi = 0; pi < interior.length; pi++) {
        const pt = interior[pi];
        let bestPos = -1;
        let bestScore = -Infinity;
        
        // Find best edge to split
        for (let i = 0; i < currentHull.length; i++) {
          const a = currentHull[i];
          const b = currentHull[(i + 1) % currentHull.length];
          const edgeDist = Math.hypot(b.x - a.x, b.y - a.y);
          const distA = Math.hypot(pt.x - a.x, pt.y - a.y);
          const distB = Math.hypot(pt.x - b.x, pt.y - b.y);
          
          // Only consider if new edges are within threshold
          if (distA <= maxEdgeLength && distB <= maxEdgeLength) {
            // Score: prefer breaking long edges, prefer points close to edge
            const reduction = edgeDist - (distA + distB);
            const score = reduction;
            if (score > bestScore) {
              bestScore = score;
              bestPos = i + 1;
            }
          }
        }
        
        if (bestPos >= 0 && bestScore > -maxEdgeLength * 0.1) {
          currentHull.splice(bestPos, 0, pt);
          interior.splice(pi, 1);
          improved = true;
          break;
        }
      }
    }
    
    return currentHull;
  };

  // Build a smooth, star-shaped concave hull by sampling directions around the centroid
  // FALLBACK - prefer computeConcaveHull for better adjacency following
  const computeRadialHull = (points: Point[], sampleCount: number, padding: number): Point[] => {
    if (points.length === 0) return points;
    const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
    const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
    const samples = Math.max(16, sampleCount);
    const twoPi = Math.PI * 2;
    const radial: Point[] = [];
    for (let i = 0; i < samples; i++) {
      const theta = (twoPi * i) / samples;
      const dx = Math.cos(theta);
      const dy = Math.sin(theta);
      let maxProj = 0;
      for (const p of points) {
        const proj = (p.x - cx) * dx + (p.y - cy) * dy;
        if (proj > maxProj) maxProj = proj;
      }
      const r = Math.max(0, maxProj + padding);
      radial.push({ x: cx + dx * r, y: cy + dy * r });
    }
    // Smooth and close
    return smoothPolygon(radial, 2);
  };

  const isPointInPolygon = (poly: Point[], point: Point): boolean => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y;
      const xj = poly[j].x, yj = poly[j].y;
      const intersect = ((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / ((yj - yi) || 1e-9) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const isPointInAnyPolygon = (polys: Point[][], p: Point): boolean => {
    for (const poly of polys) {
      if (isPointInPolygon(poly, p)) return true;
    }
    return false;
  };

  const buildCapsule = (a: Point, b: Point, radius: number): Point[] => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const vx = -uy;
    const vy = ux;
    // Construct a higher-resolution capsule polygon for smoother masks
    const steps = 18;
    const poly: Point[] = [];
    // Front arc around point b
    for (let i = 0; i <= steps; i++) {
      const t = (Math.PI * i) / steps;
      const cx = b.x + Math.cos(t) * radius * vx + Math.sin(t) * radius * vy;
      const cy = b.y + Math.cos(t) * radius * -vy + Math.sin(t) * radius * vx;
      poly.push({ x: cx, y: cy });
    }
    // Back arc around point a
    for (let i = 0; i <= steps; i++) {
      const t = (Math.PI * i) / steps;
      const cx = a.x + Math.cos(t) * radius * -vx + Math.sin(t) * radius * -vy;
      const cy = a.y + Math.cos(t) * radius * vy + Math.sin(t) * radius * -vx;
      poly.push({ x: cx, y: cy });
    }
    return smoothPolygon(poly, 1);
  };

  // Derived regions (MVP-informed):
  // - Imperial Core: systems named "Imperial Bastion" or throne world (SystemType.ThroneWorld/ImperialCore)
  // - House Worlds: systems with names containing "Sector " or "Neutral Sector" (excluding core)
  // - Wildspace: everything else (or explicitly SystemType.Wildspace)
  type RegionInfo = {
    name: 'Imperial Core' | 'House Worlds' | 'Wildspace';
    systems: StarSystem[];
    hull: { pts: Point[]; d: string | null };
    color: string;
    opacity: number;
    cx: number;
    cy: number;
    avgRadius: number;
  };
  const { regions, galaxyCenter } = (() => {
    const pts = gameState.systems.map(s => s.position);
    if (!pts.length) return { regions: [] as RegionInfo[], galaxyCenter: { x: 0, y: 0 } };
    const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
    const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length;

    // Region membership based on SystemType (robust, independent of naming)
    const coreCandidates = gameState.systems.filter(
      s => s.type === SystemType.ThroneWorld || s.type === SystemType.ImperialCore
    );
    const houseWorlds = gameState.systems.filter(
      s => s.type === SystemType.Imperial
    );
    const wild = gameState.systems.filter(
      s => s.type === SystemType.Wildspace
    );

    const mk = (name: RegionInfo['name'], list: StarSystem[], pad: number, color: string, opacity: number): RegionInfo | null => {
      if (!list.length) return null;
      const listPts = list.map(s => ({ x: s.position.x, y: s.position.y }));
      const hullPts = listPts.length >= 3 ? computeRadialHull(listPts, 48, pad) : listPts;
      const d = listPts.length >= 3 ? polygonPath(hullPts) : null;
      const rcx = listPts.reduce((a, p) => a + p.x, 0) / listPts.length;
      const rcy = listPts.reduce((a, p) => a + p.y, 0) / listPts.length;
      const avgR = listPts.reduce((sum, p) => sum + Math.hypot(p.x - cx, p.y - cy), 0) / listPts.length;
      return { name, systems: list, hull: { pts: hullPts, d }, color, opacity, cx: rcx, cy: rcy, avgRadius: avgR };
    };

    const out: RegionInfo[] = [];
    const core = mk('Imperial Core', coreCandidates, 50, '#a3e635', 0.06);
    if (core) out.push(core);
    const imp = mk('House Worlds', houseWorlds, 70, '#38bdf8', 0.05);
    if (imp) out.push(imp);
    const ws = mk('Wildspace', wild, 70, '#a78bfa', 0.05);
    if (ws) out.push(ws);
    return { regions: out, galaxyCenter: { x: cx, y: cy } };
  })();

  // Display positions: push House Worlds outward from Imperial Core centroid for legibility
  const HOUSE_WORLD_PUSH = 50; // pixels
  const coreRegion = regions.find(r => r.name === 'Imperial Core');
  const houseRegion = regions.find(r => r.name === 'House Worlds');
  const displayPosById = new Map<number, { x: number; y: number }>();
  for (const s of gameState.systems) {
    displayPosById.set(s.id, { x: s.position.x, y: s.position.y });
  }
  if (coreRegion && houseRegion) {
    const cx = coreRegion.cx;
    const cy = coreRegion.cy;
    for (const s of houseRegion.systems) {
      const base = displayPosById.get(s.id)!;
      const dx = base.x - cx;
      const dy = base.y - cy;
      const len = Math.hypot(dx, dy) || 1;
      const nx = dx / len;
      const ny = dy / len;
      displayPosById.set(s.id, { x: base.x + nx * HOUSE_WORLD_PUSH, y: base.y + ny * HOUSE_WORLD_PUSH });
    }
  }

  const getPos = (id: number) => displayPosById.get(id) || { x: 0, y: 0 };

  // Recompute region draw data from display positions so labels/contours follow spacing
  const regionsDraw = regions.map(r => {
    const pts = r.systems.map(s => getPos(s.id));
    let d: string | null = null;
    let hullPts: Point[] = [];
    const getFillPad = (name: typeof r.name): number => {
      if (name === 'Imperial Core') return 28; // small halo past inner ring
      if (name === 'House Worlds') return 36;  // a touch more to wrap the band
      return 44; // Wildspace a little farther out
    };
    if (pts.length >= 1) {
      // Always build a hull from actual system display positions, even for 1–2 points
      hullPts = computeRadialHull(pts, 48, getFillPad(r.name));
      d = polygonPath(hullPts);
    }
    const rcx = pts.reduce((a, p) => a + p.x, 0) / (pts.length || 1);
    const rcy = pts.reduce((a, p) => a + p.y, 0) / (pts.length || 1);
    const avgR = pts.reduce((sum, p) => sum + Math.hypot(p.x - galaxyCenter.x, p.y - galaxyCenter.y), 0) / (pts.length || 1);
    return { ...r, hull: { pts: hullPts, d }, cx: rcx, cy: rcy, avgRadius: avgR };
  });

  // Compute label anchor positions to avoid overlap at center
  const coreDraw = regionsDraw.find(r => r.name === 'Imperial Core');
  const coreCenter = coreDraw ? { x: coreDraw.cx, y: coreDraw.cy } : galaxyCenter;

  // Geometry helpers for label placement


  // North-arc label helpers (place curved text at the emptiest angle on the north side)
  const allSystemPtsForLabels: Point[] = gameState.systems.map(s => getPos(s.id));
  const galaxyMaxRadius = allSystemPtsForLabels.reduce((m, p) => Math.max(m, Math.hypot(p.x - coreCenter.x, p.y - coreCenter.y)), 0);
  const minDistToSystems = (p: Point): number => {
    let d = Infinity;
    for (const q of allSystemPtsForLabels) {
      const dist = Math.hypot(p.x - q.x, p.y - q.y);
      if (dist < d) d = dist;
    }
    return d;
  };
  const deg = (v: number) => (v * Math.PI) / 180;
  const arcPoint = (cx: number, cy: number, r: number, a: number): Point => ({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  function buildArcPathMid(cx: number, cy: number, r: number, midAngleRad: number, sweepDeg: number): string {
    const half = deg(sweepDeg) / 2;
    const a1 = midAngleRad - half;
    const a2 = midAngleRad + half;
    const p1 = arcPoint(cx, cy, r, a1);
    const p2 = arcPoint(cx, cy, r, a2);
    const laf = sweepDeg > 180 ? 1 : 0;
    const sf = 1; // clockwise
    return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${laf} ${sf} ${p2.x} ${p2.y}`;
  }
  function pickBestNorthAngle(cx: number, cy: number, r: number, sweepDeg: number): number {
    // Search -120° to -60° for emptiest arc
    let bestAng = -Math.PI / 2;
    let bestScore = -Infinity;
    for (let i = 0; i <= 24; i++) {
      const a = deg(-120 + (i * (60 / 24)));
      // Sample 9 points along arc to evaluate clearance
      let minClear = Infinity;
      for (let s = 0; s <= 8; s++) {
        const t = -0.5 + (s / 8);
        const pt = arcPoint(cx, cy, r, a + t * deg(sweepDeg));
        const d = minDistToSystems(pt);
        if (d < minClear) minClear = d;
      }
      // Slightly prefer closer to exact north (penalty by angular distance)
      const northPenalty = Math.abs(a - (-Math.PI / 2)) * 2;
      const score = minClear - northPenalty;
      if (score > bestScore) { bestScore = score; bestAng = a; }
    }
    return bestAng;
  }
  function pickBestNorthRadius(cx: number, cy: number, minR: number, maxR: number, sweepDeg: number): number {
    const mid = -Math.PI / 2;
    let bestR = minR;
    let bestScore = -Infinity;
    const steps = Math.max(8, Math.floor((maxR - minR) / 6));
    for (let i = 0; i <= steps; i++) {
      const r = minR + (i * (maxR - minR)) / (steps || 1);
      // Sample along arc at this radius
      let minClear = Infinity;
      for (let s = 0; s <= 8; s++) {
        const t = -0.5 + (s / 8);
        const pt = arcPoint(cx, cy, r, mid + t * deg(sweepDeg));
        const d = minDistToSystems(pt);
        if (d < minClear) minClear = d;
      }
      // penalize being outside galaxy bounds
      const boundPenalty = r > galaxyMaxRadius + 40 ? (r - (galaxyMaxRadius + 40)) : 0;
      const score = minClear - boundPenalty;
      if (score > bestScore) { bestScore = score; bestR = r; }
    }
    return bestR;
  }

  useEffect(() => {
    const updateSize = () => {
      const el = svgRef.current;
      if (!el) return;
      const parent = el.parentElement;
      const w = parent ? parent.clientWidth : el.clientWidth;
      const h = parent ? parent.clientHeight : el.clientHeight;
      if (w && h) {
        setViewport(prev => (prev.width !== w || prev.height !== h) ? { width: w, height: h } : prev);
      }
    };
    updateSize();
    parentElRef.current = svgRef.current?.parentElement || null;
    let attachedResize = false;
    if (typeof window !== 'undefined' && (window as any).ResizeObserver) {
      const obs: ResizeObserver = new (window as any).ResizeObserver(() => updateSize());
      roRef.current = obs;
      if (parentElRef.current) obs.observe(parentElRef.current);
    } else if (typeof window !== 'undefined') {
      const win: Window = window;
      win.addEventListener('resize', updateSize);
      attachedResize = true;
    }
    return () => {
      const obs = roRef.current;
      const parent = parentElRef.current;
      if (obs && parent) {
        try { obs.unobserve(parent); } catch {}
      }
      if (obs) {
        try { obs.disconnect(); } catch {}
      }
      roRef.current = null;
      parentElRef.current = null;
      if (attachedResize && typeof window !== 'undefined') {
        const win: Window = window;
        win.removeEventListener('resize', updateSize);
      }
    };
  }, []);

  const handleWheel: React.WheelEventHandler<SVGSVGElement> = (e) => {
    const delta = -e.deltaY;
    const zoomFactor = Math.exp(delta * 0.0015);
    const newScale = Math.min(3, Math.max(0.2, scale * zoomFactor));
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const Sx = e.clientX - rect.left;
    const Sy = e.clientY - rect.top;
    const Cx = viewport.width / 2;
    const Cy = viewport.height / 2;
    const r = newScale / scale;
    const newOffset = {
      x: (1 - r) * (Sx - Cx) + r * offset.x,
      y: (1 - r) * (Sy - Cy) + r * offset.y,
    };
    setScale(newScale);
    setOffset(newOffset);
  };

  const handleMouseDown: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (e.button !== 0) return; // left button only for panning
    panningRef.current = true;
    setIsPanning(true);
    didPanRef.current = false;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove: React.MouseEventHandler<SVGSVGElement> = (e) => {
    if (!panningRef.current || !lastPosRef.current) return;
    const dx = e.clientX - lastPosRef.current.x;
    const dy = e.clientY - lastPosRef.current.y;
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    if (Math.abs(dx) + Math.abs(dy) > 0) didPanRef.current = true;
    lastPosRef.current = { x: e.clientX, y: e.clientY };
  };

  const endPan = () => {
    panningRef.current = false;
    setIsPanning(false);
    lastPosRef.current = null;
  };
  
  // Group fleets by location for proper positioning
  const fleetsByLocation = new Map<number, Fleet[]>();
  gameState.fleets.forEach(fleet => {
    if (!fleet.inTransit) {
      const existing = fleetsByLocation.get(fleet.location) || [];
      existing.push(fleet);
      fleetsByLocation.set(fleet.location, existing);
    }
  });

  // Stable ownership hash - only recalc when territory ownership changes
  const ownershipHash = useMemo(() => {
    if (!gameState?.systems) return '';
    return gameState.systems.map(s => `${s.id}:${s.owner}`).join('|');
  }, [gameState?.systems]);

  const [useFieldBorders, setUseFieldBorders] = useState<boolean>(true);

  // v2 borders are always on; remove legacy toggle & storage

  const borderWorkerRef = useRef<Worker | null>(null);
  const latestJobRef = useRef(0);
  const [fieldSolution, setFieldSolution] = useState<{ polygons: BorderPolygon[]; debug?: any } | null>(null);
  const [fieldStatus, setFieldStatus] = useState<'idle' | 'working' | 'error'>('idle');

  useEffect(() => {
    if (!useFieldBorders) {
      if (borderWorkerRef.current) {
        borderWorkerRef.current.terminate();
        borderWorkerRef.current = null;
      }
      setFieldSolution(null);
      setFieldStatus('idle');
      return;
    }
    const worker = createBorderWorker();
    borderWorkerRef.current = worker;
    worker.onmessage = (event: MessageEvent) => {
      const { type, jobId, payload } = event.data || {};
      if (type === 'result') {
        if (jobId && jobId !== latestJobRef.current) return;
        setFieldSolution({ polygons: payload.polygons, debug: payload.debug });
        setFieldStatus('idle');
      } else if (type === 'error') {
        if (jobId && jobId !== latestJobRef.current) return;
        console.error('Border worker error:', payload?.message || payload);
        setFieldStatus('error');
        setFieldSolution(null);
      }
    };
    worker.onerror = (err) => {
      console.error('Border worker crashed:', err);
      setFieldStatus('error');
      setFieldSolution(null);
    };
    return () => {
      worker.terminate();
      if (borderWorkerRef.current === worker) borderWorkerRef.current = null;
    };
  }, [useFieldBorders]);

  const baseFieldOptions = useMemo<BorderOptions>(() => ({
    metric: 'cost-field',
    gridTileSize: 256,
    gridMetersPerCell: 60,
    gridMaxDim: 512,
    neutralThreshold: 0.7,
    shareThreshold: 0.2,
    hysteresis: 0.35,
    hysteresisMargin: 0.35,
    minCorridorWorld: 600,
    openRadiusWorld: 800,
    minComponentAreaWorld: 120000,
    maxFilamentAspect: 12,
    softenRadiusWorld: 320,
    softenIterations: 1,
    softenThreshold: 0.25,
    blurRadius: 0,
    blurPasses: 0,
    simplifyEpsilon: 18,
    smoothIterations: 2,
    smoothPreserveRadius: 600,
    polygonOffset: 12,
    hyperlaneCost: 1,
    defaultCost: 1,
  }), []);

  // Adaptive quality by zoom for broad device support
  const scaleBucket = useMemo<'far' | 'mid' | 'near'>(() => {
    if (scale < 0.55) return 'far';
    if (scale < 1.35) return 'mid';
    return 'near';
  }, [scale]);
  const computeDynamicOptions = (base: BorderOptions, bucket: 'far' | 'mid' | 'near'): BorderOptions => {
    const gmc = bucket === 'near' ? 40 : bucket === 'mid' ? 60 : 110;
    const smoothIter = bucket === 'near' ? 3 : 2;
    const eps = Math.max(10, Math.round(18 / Math.max(0.5, Math.min(2.5, scale))));
    const gridMaxDim = bucket === 'near' ? 768 : 512;
    return {
      ...base,
      gridMetersPerCell: gmc,
      smoothIterations: smoothIter,
      simplifyEpsilon: eps,
      gridMaxDim,
      polygonOffset: 12,
      minCorridorWorld: 600,
      openRadiusWorld: 800,
      minComponentAreaWorld: 120000,
      maxFilamentAspect: 12,
      softenRadiusWorld: 320,
      softenIterations: 1,
    };
  };
  const qualitySigRef = useRef<string>('');
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!useFieldBorders || !showEmpireBorders) return;
    if (!gameState?.systems?.length) {
      setFieldSolution(null);
      return;
    }
    const worker = borderWorkerRef.current;
    if (!worker) return;

    const opts = computeDynamicOptions(baseFieldOptions, scaleBucket);

    const systemsLite = gameState.systems.map(s => {
      const dp = getPos(s.id);
      const kind: 'capital' | 'colony' | 'bastion' | 'outpost' | 'station' =
        (s.type === SystemType.ThroneWorld || s.type === SystemType.ImperialCore) ? 'capital'
        : ((s.hasShipyard || (s as any).isStronghold) ? 'bastion'
        : ((s.population ?? 0) > 0 ? 'colony' : 'outpost'));
      const owner = (() => {
        if (s.owner === 'Unclaimed') return undefined;
        if (s.owner === 'NPC') {
          const fid = (s as any).npcFactionId as string | undefined;
          return fid ? `NPC:${fid}` : undefined;
        }
        return String(s.owner || '');
      })();
      return {
        id: String(s.id),
        pos: { x: dp.x, y: dp.y },
        kind,
        owner,
        supplyOnline: true,
        strengthBase: undefined,
        population: s.population,
      };
    });
    const empiresPayload = [
      ...gameState.players.map(p => ({ id: String(p.id), name: p.name, color: (p.color as string) || '#66bbff' })),
      ...((gameState as any).mapMeta?.npcFactions || []).map((f: any) => ({ id: `NPC:${f.id}`, name: f.name, color: f.color || '#ff6666' })),
    ];

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const s of gameState.systems) {
      const p = getPos(s.id);
      minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
    }
    if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
      setFieldSolution(null);
      return;
    }
    const pad = 240;
    const bbox = { min: { x: minX - pad, y: minY - pad }, max: { x: maxX + pad, y: maxY + pad } };

    const hyper: { aId: string; bId: string }[] = [];
    const edgeSet = new Set<string>();
    for (const s of gameState.systems) {
      for (const nid of s.adjacentSystems) {
        const key = `${Math.min(s.id, nid)}-${Math.max(s.id, nid)}`;
        if (edgeSet.has(key)) continue;
        edgeSet.add(key);
        hyper.push({ aId: String(Math.min(s.id, nid)), bId: String(Math.max(s.id, nid)) });
      }
    }

    // Build quality signature to avoid redundant recomputes
    const qsig = `${scaleBucket}|${opts.gridMetersPerCell}|${opts.smoothIterations}|${opts.simplifyEpsilon}|${bbox.min.x.toFixed(0)},${bbox.min.y.toFixed(0)},${bbox.max.x.toFixed(0)},${bbox.max.y.toFixed(0)}`;
    if (qualitySigRef.current === qsig) return;
    qualitySigRef.current = qsig;

    // Debounce posting compute jobs to avoid thrash on quick interactions
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
    const jobId = ++latestJobRef.current;
    setFieldStatus('working');
    worker.postMessage({
      type: 'compute',
      jobId,
      payload: {
        systems: systemsLite,
        empires: empiresPayload,
        bbox,
          options: opts,
        hyperlanes: hyper,
      },
    });
    }, 60);
  }, [useFieldBorders, showEmpireBorders, ownershipHash, scaleBucket, gameState.players, baseFieldOptions, gameState.systems, getPos]);

  const territorySolution = useFieldBorders ? fieldSolution : null;
  function computeLabelPos(r: typeof regionsDraw[number]): { x: number; y: number } {
    if (r.name === 'Imperial Core') return { x: r.cx, y: r.cy };
    const vx = r.cx - coreCenter.x;
    const vy = r.cy - coreCenter.y;
    const len = Math.hypot(vx, vy);
    const nx = len > 1 ? vx / len : 0;
    const ny = len > 1 ? vy / len : -1; // default up if concentric
    const base = r.name === 'House Worlds' ? Math.max(40, r.avgRadius * 0.25) : Math.max(60, r.avgRadius * 0.35);
    return { x: r.cx + nx * base, y: r.cy + ny * base };
  }

  // Geometry helpers for label placement
  function polygonCentroidWeighted(pts: Point[]): { x: number; y: number } {
    // Area-weighted centroid for polygon
    if (pts.length < 3) return { x: 0, y: 0 };
    let a = 0; let cx = 0; let cy = 0;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const q = pts[(i + 1) % pts.length];
      const f = p.x * q.y - q.x * p.y;
      a += f;
      cx += (p.x + q.x) * f;
      cy += (p.y + q.y) * f;
    }
    a = a * 0.5 || 1;
    cx = cx / (6 * a);
    cy = cy / (6 * a);
    return { x: cx, y: cy };
  }

  function buildTopArcPath(pts: Point[], center: { x: number; y: number }, widthDeg = 120): string | null {
    if (pts.length < 3) return null;
    const angles = pts.map((p, i) => ({ i, ang: Math.atan2(p.y - center.y, p.x - center.x) }));
    const top = -Math.PI / 2;
    const half = (widthDeg * Math.PI) / 180 / 2;
    const pick = angles.filter(a => {
      let d = Math.atan2(Math.sin(a.ang - top), Math.cos(a.ang - top));
      return Math.abs(d) <= half;
    }).map(a => a.i);
    let idxs: number[];
    if (pick.length >= 2) {
      idxs = pick;
    } else {
      // fallback: take 1/6 hull around the top-most point
      let topIdx = 0;
      for (let j = 1; j < angles.length; j++) {
        if (angles[j].ang < angles[topIdx].ang) topIdx = j;
      }
      const span = Math.max(3, Math.floor(pts.length / 6));
      idxs = [];
      for (let k = -Math.floor(span / 2); k <= Math.floor(span / 2); k++) idxs.push((topIdx + k + pts.length) % pts.length);
    }
    idxs.sort((a, b) => a - b);
    const seg = idxs.map(i => pts[i]);
    const d = 'M ' + seg.map(p => `${p.x} ${p.y}`).join(' L ');
    return d;
  }

  function buildCirclePath(cx: number, cy: number, r: number): string {
    // Two-arc circle path for textPath compatibility
    return `M ${cx - r} ${cy} a ${r} ${r} 0 1 1 ${r * 2} 0 a ${r} ${r} 0 1 1 -${r * 2} 0`;
  }

  function computeCoreLabelPos(r: typeof regionsDraw[number]): { x: number; y: number } {
    // Pick a position near core centroid that avoids overlap with systems
    const fontSize = 22; const w = r.name.length * fontSize * 0.6; const h = fontSize;
    const minDist = Math.max(26, h * 0.9);
    const systems = r.systems.map(s => getPos(s.id));
    const C = { x: r.cx, y: r.cy };
    const candidates: { x: number; y: number }[] = [];
    const R = 24;
    for (let k = 0; k < 12; k++) {
      const ang = (-Math.PI / 2) + (k * Math.PI / 6);
      candidates.push({ x: C.x + Math.cos(ang) * R, y: C.y + Math.sin(ang) * R });
    }
    function score(p: { x: number; y: number }) {
      let dmin = Infinity;
      for (const s of systems) dmin = Math.min(dmin, Math.hypot(p.x - s.x, p.y - s.y));
      return dmin;
    }
    let best = C; let bestScore = -Infinity;
    for (const cand of [C, ...candidates]) {
      const sc = score(cand);
      if (sc > bestScore) { best = cand; bestScore = sc; }
    }
    return best;
  }

  // North-arc label helpers (place curved text at the emptiest angle on the north side)
  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${viewport.width} ${viewport.height}`}
      style={{ backgroundColor: 'transparent', cursor: isPanning ? 'grabbing' : 'default' }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endPan}
      onMouseLeave={endPan}
      onClick={() => { if (!isPanning && !didPanRef.current) { onEmptyClick && onEmptyClick(); } }}
    >
      <defs>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        {/* Enhanced territory border glow for depth */}
        <filter id="territoryGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blur1" />
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur2" />
          <feColorMatrix in="blur1" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.3 0" result="glow1" />
          <feColorMatrix in="blur2" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.5 0" result="glow2" />
          <feMerge>
            <feMergeNode in="glow1" />
            <feMergeNode in="glow2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        {/* System halos */}
        <radialGradient id="haloAlly" r="1">
          <stop offset="0%" stopColor="rgba(77,208,225,0.35)" />
          <stop offset="70%" stopColor="rgba(77,208,225,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="haloEnemy" r="1">
          <stop offset="0%" stopColor="rgba(255,107,107,0.35)" />
          <stop offset="70%" stopColor="rgba(255,107,107,0.12)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="haloNeutral" r="1">
          <stop offset="0%" stopColor="rgba(230,237,243,0.25)" />
          <stop offset="80%" stopColor="rgba(230,237,243,0.08)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        {/* Animated route gradient */}
        <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00e5ff" stopOpacity="0" />
          <stop offset="50%" stopColor="#00e5ff" stopOpacity="0.75" />
          <stop offset="100%" stopColor="#00e5ff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g transform={`translate(${viewport.width / 2 + offset.x}, ${viewport.height / 2 + offset.y}) scale(${scale})`}>
        {/* Region shading (MVP-informed) */}
        {showRegionLabels && regionsDraw.map((r, i) => (
          <path key={`region-${i}`} d={r.hull.d || ''} fill={r.color} fillOpacity={r.opacity} stroke={r.color} strokeOpacity={r.opacity + 0.05} strokeWidth={3} style={{ pointerEvents: 'none' }} />
        ))}
        {/* Region labels */}
        {showRegionLabels && regionsDraw.map((r, i) => {
          if (r.name === 'House Worlds') {
            // Keep on north side, choose mid angle with max clearance in [-120,-60]
            const baseR = Math.max(80, r.avgRadius * 0.92);
            const midAng = pickBestNorthAngle(coreCenter.x, coreCenter.y, baseR, 120);
            const arcD = buildArcPathMid(coreCenter.x, coreCenter.y, baseR, midAng, 120);
            const arcId = `house-arc-${i}`;
            return (
              <g key={`region-label-${i}`} style={{ pointerEvents: 'none' }}>
                <path id={arcId} d={arcD} fill="none" stroke="transparent" />
                <text fill="#e5e7eb" opacity={0.95} fontSize={22} fontWeight={800} stroke="#000" strokeWidth={5} style={{ paintOrder: 'stroke' as const }}>
                  <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
                    {r.name}
                  </textPath>
                </text>
              </g>
            );
          }
          if (r.name === 'Wildspace') {
            // Force label along exact north (midAng = -90deg), choose best radius for empty space
            const minR = Math.max(120, r.avgRadius * 1.12);
            const maxR = Math.min(galaxyMaxRadius + 60, minR + 160);
            const radius = pickBestNorthRadius(coreCenter.x, coreCenter.y, minR, maxR, 120);
            const midAng = -Math.PI / 2;
            const arcD = buildArcPathMid(coreCenter.x, coreCenter.y, radius, midAng, 120);
            const arcId = `wild-arc-${i}`;
            return (
              <g key={`region-label-${i}`} style={{ pointerEvents: 'none' }}>
                <path id={arcId} d={arcD} fill="none" stroke="transparent" />
                <text fill="#e5e7eb" opacity={0.9} fontSize={22} fontWeight={800} stroke="#000" strokeWidth={5} style={{ paintOrder: 'stroke' as const }}>
                  <textPath href={`#${arcId}`} startOffset="50%" textAnchor="middle">
                    {r.name}
                  </textPath>
                </text>
              </g>
            );
          }
          const lp = r.name === 'Imperial Core' ? computeCoreLabelPos(r) : computeLabelPos(r);
          return (
            <g key={`region-label-${i}`} transform={`translate(${lp.x}, ${lp.y})`} style={{ pointerEvents: 'none' }}>
              <text x={0} y={0} textAnchor="middle" fill="#e5e7eb" opacity={0.95} fontSize={22} fontWeight={800} stroke="#000" strokeWidth={5} style={{ paintOrder: 'stroke' as const }}>
                {r.name}
              </text>
            </g>
          );
        })}

        {/* Empire borders via v2 solver only (no legacy fallback) */}
        {showEmpireBorders && (() => {
          const polygonsToUse = territorySolution?.polygons && territorySolution.polygons.length > 0 ? territorySolution.polygons : null;
          if (useFieldBorders && polygonsToUse) {
            // Build per-empire cluster hull clip paths to prevent long corridors
            const clustersByEmpire = new Map<string, Point[][]>();
            const idToSystem = new Map(gameState.systems.map(s => [s.id, s] as [number, StarSystem]));
          const owners = new Map<string, StarSystem[]>();
          for (const s of gameState.systems) {
              const owner = (s.owner ?? 'Unclaimed').toString();
              if (owner === 'Unclaimed') continue;
              const arr = owners.get(owner) || []; arr.push(s); owners.set(owner, arr);
            }
            owners.forEach((list, owner) => {
            const visited = new Set<number>();
              const comps: Point[][] = [];
            for (const s of list) {
              if (visited.has(s.id)) continue;
                const q: StarSystem[] = [s]; visited.add(s.id);
                const pts: Point[] = [];
                while (q.length) {
                  const cur = q.shift()!;
                  const p = getPos(cur.id);
                  pts.push({ x: p.x, y: p.y });
                for (const nid of cur.adjacentSystems) {
                  const n = idToSystem.get(nid);
                    if (!n || n.owner !== cur.owner || visited.has(n.id)) continue;
                    visited.add(n.id); q.push(n);
                  }
                }
                if (pts.length) comps.push(pts);
              }
              clustersByEmpire.set(owner, comps);
            });

            const clipDefs: React.ReactNode[] = [];
            const clipIds = new Map<string, string>();
            Array.from(clustersByEmpire.entries()).forEach(([empireId, comps], ei) => {
              const id = `emp-clip-${empireId.replace(/[^a-zA-Z0-9]/g, '-')}-${ei}`;
              clipIds.set(empireId, id);
              const hullPaths = comps.map((pts, ci) => {
                const pad = 40; // screen-space padding
                const hull = pts.length >= 3 ? computeConcaveHull(pts, 120) : pts;
                const padded = expandPolygon(hull, pad);
                const path = 'M ' + padded.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z';
                return <path key={`hp-${ci}`} d={path} />;
              });
              clipDefs.push(
                <clipPath id={id} key={id} clipPathUnits="userSpaceOnUse">
                  {hullPaths}
                </clipPath>
              );
            });

            return [
              <defs key="empire-clip-defs">{clipDefs}</defs>,
              ...polygonsToUse.map((poly, idx) => {
              const baseColor = getOwnerColor(poly.empireId);
              const fillColor = adjustColor(baseColor, 0.9);
              const rimColor = adjustColor(baseColor, 1.15);
              const fillPath = 'M ' + poly.exterior.map(pt => `${pt.x} ${pt.y}`).join(' L ') + ' Z';
              const holes = poly.holes.map(h => ' M ' + h.map(pt => `${pt.x} ${pt.y}`).join(' L ') + ' Z').join('');
              const fullPath = fillPath + holes;
              const textureId = `borderTexture-${poly.empireId}-${idx}`;
                const clipId = clipIds.get(poly.empireId);
                  return (
                <g key={`empire-field-${poly.empireId}-${idx}`} style={{ pointerEvents: 'none' }} clipPath={clipId ? `url(#${clipId})` : undefined}>
                  <defs>
                    <radialGradient id={`${textureId}-radial`} cx="50%" cy="50%" r="65%">
                      <stop offset="0%" stopColor={fillColor} stopOpacity="0.35" />
                      <stop offset="55%" stopColor={fillColor} stopOpacity="0.22" />
                      <stop offset="100%" stopColor={baseColor} stopOpacity="0.05" />
                    </radialGradient>
                  </defs>
                  {/* Soft fill */}
                  <path d={fullPath} fillRule="evenodd" fill={`url(#${textureId}-radial)`} stroke="none" />
                  <path d={fullPath} fillRule="evenodd" fill={baseColor} fillOpacity={0.24} stroke="none" style={{ mixBlendMode: 'screen' as const }} />
                  {/* Hard outline (dual pass: under black, top color) */}
                  <path d={fillPath} fill="none" stroke="#000" strokeWidth={2.8} strokeOpacity={0.25} vectorEffect="non-scaling-stroke" />
                  <path d={fillPath} fill="none" stroke={rimColor} strokeWidth={1.8} strokeOpacity={0.85} vectorEffect="non-scaling-stroke" />
                          </g>
                        );
            })
            ];
          }

          // If no polygons from v2, render nothing; we do not fallback to capsules
          return null;
        })()}

        {/* Legacy simple borders disabled (replaced by advanced territory layer) */}
        {false && (() => {
          const owners = new Map<string, StarSystem[]>();
          for (const s of gameState.systems) {
            const key = (s.owner ?? 'Unclaimed').toString();
            const arr = owners.get(key) || [];
            arr.push(s);
            owners.set(key, arr);
          }
          const results: Array<{ owner: string; clusters: StarSystem[][] }> = [];
          owners.forEach((list, owner) => {
            if (owner === 'NPC' || owner === 'Unclaimed') return;
            const idToSystem = new Map(list.map(s => [s.id, s] as [number, StarSystem]));
            const visited = new Set<number>();
            const clusters: StarSystem[][] = [];
            for (const s of list) {
              if (visited.has(s.id)) continue;
              const q: StarSystem[] = [s];
              visited.add(s.id);
              const comp: StarSystem[] = [];
              while (q.length) {
                const cur = q.shift()!;
                comp.push(cur);
                for (const nid of cur.adjacentSystems) {
                  const n = idToSystem.get(nid);
                  if (!n || visited.has(n.id)) continue;
                  visited.add(n.id);
                  q.push(n);
                }
              }
              clusters.push(comp);
            }
            results.push({ owner, clusters });
          });

          return results.map(({ owner, clusters }) => {
            const isMine = owner === myPlayerId;
            const color = isMine ? 'var(--ally)' : 'var(--enemy)';
            const pad = isMine ? 28 : 16;

            return (
              <g key={`territory-borders-${owner}`} style={{ pointerEvents: 'none' }}>
                {clusters.map((cluster, idx) => {
                  const idSet = new Set(cluster.map(s => s.id));
                  const pts: { x: number; y: number }[] = [];
                  for (const s of cluster) {
                    const p = { x: s.position.x, y: s.position.y };
                    const r = Math.max(8, pad * 0.7);
                    const circle = smoothPolygon(
                      expandPolygon(
                        computeConvexHull([
                          { x: p.x + r, y: p.y },
                          { x: p.x - r, y: p.y },
                          { x: p.x, y: p.y + r },
                          { x: p.x, y: p.y - r },
                        ]),
                        0
                      ),
                      2
                    );
                    pts.push(...circle);
                  }
                  for (const s of cluster) {
                    for (const nid of s.adjacentSystems) {
                      if (!idSet.has(nid) || s.id > nid) continue;
                      const a = { x: s.position.x, y: s.position.y };
                      const bSys = systemMap.get(nid)!;
                      const b = { x: bSys.position.x, y: bSys.position.y };
                      const seg = buildCapsule(a, b, pad);
                      pts.push(...seg);
                    }
                  }
                  const samples = Math.min(64, Math.max(24, Math.floor(pts.length / 4)));
                  const hullPts = computeRadialHull(pts, samples, pad * 0.5);
                  const outline = polygonPath(hullPts);
                  // Compute enclave holes (neutral/enemy systems) within my territory hulls
                  const holes = isMine
                    ? gameState.systems
                        .filter(s => s.owner !== owner && isPointInPolygon(hullPts, { x: s.position.x, y: s.position.y }))
                        .map(s => ({ x: s.position.x, y: s.position.y, r: Math.max(18, pad * 1.5 + 6) }))
                    : [];
                  return (
                    <g key={`tb-${owner}-${idx}`}>
                      <path
                        d={outline}
                        fill="none"
                        stroke={color}
                        strokeOpacity={isMine ? 0.35 : 0.40}
                        strokeWidth={isMine ? 2.2 : 1.6}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />
                      {holes.map((h, i) => (
                        <circle
                          key={`tb-hole-${owner}-${idx}-${i}`}
                          cx={h.x}
                          cy={h.y}
                          r={h.r}
                          fill="none"
                          stroke={color}
                          strokeOpacity={0.35}
                          strokeWidth={2}
                        />
                      ))}
                    </g>
                  );
                })}
              </g>
            );
          });
        })()}

        {/* Phase lanes / adjacency lines under systems. Fog is beneath this group. */}
        {(() => {
          // Build unique undirected edges to draw every connection regardless of one-way adjacency inputs
          const edgeMap = new Map<string, { a: StarSystem; b: StarSystem }>();
          for (const system of gameState.systems) {
            for (const adjId of system.adjacentSystems) {
            const adjacentSystem = systemMap.get(adjId);
              if (!adjacentSystem) continue;
              const key = `${Math.min(system.id, adjacentSystem.id)}-${Math.max(system.id, adjacentSystem.id)}`;
              if (!edgeMap.has(key)) edgeMap.set(key, { a: system, b: adjacentSystem });
            }
          }
          return Array.from(edgeMap.entries()).map(([key, { a, b }]) => {
            const isHovered = hoveredSystemId != null && (hoveredSystemId === a.id || hoveredSystemId === b.id);
            const isSelected = selectedSystemId != null && (selectedSystemId === a.id || selectedSystemId === b.id);
            let isPath = false;
            for (const fl of gameState.fleets) {
              if (!fl.inTransit || fl.origin == null || fl.destination == null) continue;
              const k = `${Math.min(fl.origin, fl.destination)}-${Math.max(fl.origin, fl.destination)}`;
              if (k === key) { isPath = true; break; }
            }
            const highlighted = isHovered || isSelected || isPath || hoveredEdgeKey === key;
            const stroke = highlighted ? '#cbd5e1' : '#64748b';
            const strokeOpacity = highlighted ? 0.9 : 0.45;
            const strokeWidth = highlighted ? 2.5 : 1.5;
            // Draw edges using display positions to align with shifted systems
            const ap = getPos(a.id);
            const bp = getPos(b.id);
            return (
              <line
                key={key}
                x1={ap.x}
                y1={ap.y}
                x2={bp.x}
                y2={bp.y}
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeOpacity={strokeOpacity}
                strokeLinecap="round"
                onMouseEnter={() => setHoveredEdgeKey(key)}
                onMouseLeave={() => setHoveredEdgeKey(null)}
                style={{ cursor: highlighted ? 'pointer' : 'default' }}
              />
            );
          });
        })()}

        {/* Draw systems (hide some labels at small scales for legibility) */}
        {gameState.systems.map(system => (
          <Tooltip
            key={`sys-tip-${system.id}`}
            asChild
            placement="top"
            offset={12}
            openDelay={200}
            closeDelay={80}
            interactive={false}
                content={
                  (() => {
                    const defaults = getPlanetTypeDefaults(system.planetType);
                    const override = getPlanetOverrideBySystem(system);
                    const maxPop = override?.maxPopulationOverride ?? system.maxPopulation ?? defaults.maxPopulation;
                    const creditsBase = (system.population ?? 0) * defaults.creditsPerPop;
                    const influenceBase = (system.population ?? 0) * defaults.influencePerPop;
                    const creditsBonusPct = override?.creditsBonusPct ?? 0;
                    const influenceBonusPct = override?.influenceBonusPct ?? 0;
                    const credits = Math.floor(creditsBase * (1 + creditsBonusPct / 100));
                    const influence = Math.floor(influenceBase * (1 + influenceBonusPct / 100));
                    const ownerName = (() => {
                      if (system.owner === 'NPC') {
                        const fac = getFactionNameForSystem(system);
                        return fac ? `NPC • ${fac}` : 'NPC';
                      }
                      if (system.owner === 'Unclaimed') return 'Unclaimed';
                      return getOwnerName(String(system.owner));
                    })();
                    return (
                      <div>
                        <div><strong>{system.name}</strong></div>
                        <div style={{ opacity: 0.9 }}>Owner: {ownerName}{system.hasShipyard ? ' • Shipyard' : ''}{(system as any).isStronghold ? ' • Stronghold' : ''}</div>
                        {typeof system.population === 'number' && (
                          <div style={{ opacity: 0.9 }}>Pop: {system.population.toFixed(1)}M / {maxPop}M</div>
                        )}
                        <div style={{ opacity: 0.95 }}>💰 +{credits}/t{creditsBonusPct ? ` (${creditsBonusPct}% bonus)` : ''}</div>
                        <div style={{ opacity: 0.95 }}>✨ +{influence}/t{influenceBonusPct ? ` (${influenceBonusPct}% bonus)` : ''}</div>
                        {override?.notes && <div style={{ opacity: 0.8, fontSize: 12, marginTop: 2 }}>{override.notes}</div>}
                      </div>
                    );
                  })()
                }
          >
            <g
              onClick={(e) => { e.stopPropagation(); onSystemClick(system); }}
              onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onSystemContextMenu && onSystemContextMenu(system, e.clientX, e.clientY); }}
              onMouseEnter={() => setHoveredSystemId(system.id)}
              onMouseLeave={() => setHoveredSystemId(null)}
              style={{ cursor: 'pointer' }}
            >
              {(() => {
                const p = getPos(system.id);
                const saved = system.position;
                // Render system at display position by temporarily translating
                return (
                  <g transform={`translate(${p.x - saved.x}, ${p.y - saved.y})`}>
                     <StarSystemComponent system={system} myPlayerId={myPlayerId} isSelected={selectedSystemId === system.id} tick={gameState.tick} getOwnerColor={getOwnerColor} getOwnerName={getOwnerName} />
                  </g>
                );
              })()}
            </g>
          </Tooltip>
        ))}

        {/* Draw fleets in transit */}
        {gameState.fleets.filter(fleet => fleet.inTransit).map(fleet => {
          if (fleet.origin === undefined || fleet.destination === undefined) return null;
          const originSystem = systemMap.get(fleet.origin);
          const destSystem = systemMap.get(fleet.destination);
          if (!originSystem || !destSystem || !fleet.arrivalTick) return null;
          
          // Calculate progress along transit route (0 to 1)
          const op = getPos(originSystem.id);
          const dp = getPos(destSystem.id);
          const dx = dp.x - op.x;
          const dy = dp.y - op.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const logicalDistance = distance / MAP_SCALE;
          // Prefer authoritative timing from server for smooth interpolation
          const departTick = (fleet as any).departTick as number | undefined;
          const travelDuration = (fleet as any).travelDuration as number | undefined;
          const fallbackDuration = Math.max(1, Math.ceil(logicalDistance / TRAVEL_DISTANCE_PER_TICK));
          const totalDuration = (typeof travelDuration === 'number' && travelDuration > 0) ? travelDuration : fallbackDuration;
          const startTick = (typeof departTick === 'number') ? departTick : Math.max(0, (fleet.arrivalTick ?? 0) - totalDuration);
          const elapsed = Math.max(0, gameState.tick - startTick);
          const progress = Math.max(0, Math.min(1, elapsed / totalDuration));
          
          const transitX = op.x + dx * progress;
          const transitY = op.y + dy * progress;
          
          const isSelected = selectedFleets.includes(fleet.id);
          const eta = Math.max(0, (fleet.arrivalTick ?? (startTick + totalDuration)) - gameState.tick);
          const etaText = `ETA ${eta}`;
          const textWidth = etaText.length * 6 + 8;
          
          return (
            <g key={fleet.id}>
              {/* Transit line glow */}
              <line
                x1={op.x}
                y1={op.y}
                x2={dp.x}
                y2={dp.y}
                stroke={fleet.owner === myPlayerId ? '#00ff00' : '#66bbff'}
                strokeWidth="6"
                opacity="0.2"
                strokeLinecap="round"
                filter="url(#glow)"
              />
              {/* Transit line */}
              <line
                x1={op.x}
                y1={op.y}
                x2={dp.x}
                y2={dp.y}
                stroke={fleet.owner === myPlayerId ? '#00ff00' : '#66bbff'}
                strokeWidth="2"
                strokeDasharray="5,3"
                opacity="0.6"
                strokeLinecap="round"
              />
              {/* Animated energy along route */}
              <line
                x1={op.x}
                y1={op.y}
                x2={dp.x}
                y2={dp.y}
                stroke="url(#routeGrad)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="60 300"
              >
                <animate attributeName="stroke-dashoffset" from="0" to="-360" dur="3s" repeatCount="indefinite" />
              </line>
              {/* Fleet in transit with countdown */}
              <Tooltip
                asChild
                placement="right"
                offset={10}
                content={
                  <div>
                    <div><strong>Fleet F{fleet.id}</strong> • {fleet.inCombat ? 'In Combat' : fleet.inTransit ? 'In Transit' : 'Ready'}</div>
                    <div>Power {Math.round(fleet.power)}/{Math.round(fleet.maxPower)} • Ships {(fleet.ships?.length ?? 0)}</div>
                    {typeof fleet.arrivalTick === 'number' && fleet.inTransit && <div>ETA t-{Math.max(0, fleet.arrivalTick - gameState.tick)}</div>}
                  </div>
                }
              >
                <g>
                  <FleetComponent
                    fleet={fleet}
                    x={transitX}
                    y={transitY - 20}
                    myPlayerId={myPlayerId}
                    isSelected={isSelected}
                    onClick={() => onFleetClick(fleet)}
                    onContextMenu={(e) => onFleetContextMenu && onFleetContextMenu(fleet, e.clientX, e.clientY)}
                    color={getOwnerColor(String(fleet.owner))}
                    icon={getOwnerIcon(String(fleet.owner))}
                    ownerName={getOwnerName(String(fleet.owner))}
                  />
                </g>
              </Tooltip>
              {/* ETA badge */}
              <g transform={`translate(${transitX}, ${transitY + 15})`}>
                <rect x={-textWidth / 2} y={-10} width={textWidth} height={14} rx={6} ry={6} fill="#000" fillOpacity={0.7} stroke="#444" />
                <text
                  x={0}
                  y={0}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#ffaa00"
                  fontWeight="bold"
                >
                  {etaText}
                </text>
              </g>
            </g>
          );
        })}

        {/* Draw fleets at systems with proper stacking */}
        {Array.from(fleetsByLocation.entries()).map(([systemId, fleets]) => {
          const locationSystem = systemMap.get(systemId);
          if (!locationSystem) return null;
          
          const lp = getPos(locationSystem.id);

          // Sort fleets for consistent display order
          const sortedFleets = [...fleets].sort((a, b) => {
            // Put player's fleets first
            if (a.owner === myPlayerId && b.owner !== myPlayerId) return -1;
            if (a.owner !== myPlayerId && b.owner === myPlayerId) return 1;
            // Then by power
            return b.power - a.power;
          });
          
          // Calculate positions for multiple fleets
          const fleetsPerRow = 4;
          const fleetSpacing = 30;
          const startY = -25;
          
          return sortedFleets.map((fleet, index) => {
            const row = Math.floor(index / fleetsPerRow);
            const col = index % fleetsPerRow;
            const totalInRow = Math.min(fleetsPerRow, sortedFleets.length - row * fleetsPerRow);
            const xOffset = (col - (totalInRow - 1) / 2) * fleetSpacing;
            const yOffset = startY - row * 25;
            
            const isSelected = selectedFleets.includes(fleet.id);
            
            return (
              <Tooltip
                key={fleet.id}
                asChild
                placement="right"
                offset={10}
                openDelay={200}
                closeDelay={80}
                interactive={false}
                content={
                  <div>
                    <div><strong>Fleet F{fleet.id}</strong> • {fleet.inCombat ? 'In Combat' : fleet.inTransit ? 'In Transit' : 'Ready'}</div>
                    <div>Owner: {fleet.owner === 'NPC' ? (() => { const fac = getFactionNameForSystem(systemMap.get(fleet.location)); return fac ? `NPC • ${fac}` : 'NPC'; })() : getOwnerName(String(fleet.owner))}</div>
                    <div>Power {Math.round(fleet.power)}/{Math.round(fleet.maxPower)} • Ships {(fleet.ships?.length ?? 0)}</div>
                  </div>
                }
              >
                <g>
                  <FleetComponent
                    fleet={fleet}
                    x={lp.x + xOffset}
                    y={lp.y + yOffset}
                    myPlayerId={myPlayerId}
                    isSelected={isSelected}
                    onClick={() => onFleetClick(fleet)}
                    onContextMenu={(e) => onFleetContextMenu && onFleetContextMenu(fleet, e.clientX, e.clientY)}
                    color={getOwnerColor(String(fleet.owner))}
                    icon={getOwnerIcon(String(fleet.owner))}
                      ownerName={getOwnerName(String(fleet.owner))}
                  />
                </g>
              </Tooltip>
            );
          });
        })}

        {/* Ping highlight (snap to nearest system's display position if close) */}
        {(() => {
          if (!ping) return null;
          let px = ping.x, py = ping.y;
          let bestId: number | null = null; let bestD = Infinity;
          for (const s of gameState.systems) {
            const sp = s.position; // original space
            const d = Math.hypot(px - sp.x, py - sp.y);
            if (d < bestD) { bestD = d; bestId = s.id; }
          }
          if (bestId != null && bestD <= 40) {
            const dp = getPos(bestId);
            px = dp.x; py = dp.y;
          }
          return (
            <g>
              <circle cx={px} cy={py} r={14} fill="none" stroke="#00e5ff" strokeWidth={2} opacity={0.9}>
                <animate attributeName="r" from="6" to="40" dur="1.5s" repeatCount="indefinite" />
                <animate attributeName="opacity" from="0.9" to="0" dur="1.5s" repeatCount="indefinite" />
              </circle>
            </g>
          );
        })()}
      </g>
    </svg>
  );
};

const createBorderWorker = () => {
  const url = new URL('./workers/borderWorker.ts', import.meta.url);
  const v =
    (import.meta as any).env?.VITE_BUILD_ID ||
    (import.meta as any).env?.VITE_COMMIT_SHA ||
    String(Math.floor(Date.now() / 3600000));
  url.searchParams.set('v', v);
  return new Worker(url, { type: 'module' });
};

export default MapComponent;
